<template>
  <div class="call-fb-charts-container">
    <ThemeProvider :theme="theme">
      <template
        v-for="chart in charts"
        :key="chart.title"
      >
        <div class="chart-container">
          <div class="chart-header">
            <span class="chart-header-title">
              {{ chart.title }}
            </span>
          </div>
          <div class="chart-content">
            <LineChart
              :data-set="dataSource"
              :data-options="chart.chartProps.dataOptions"
              :style-options="chart.chartProps.styleOptions"
              :filters="chart.chartProps.filters"
            />
          </div>
        </div>
      </template>
    </ThemeProvider>
  </div>
</template>

<script lang="ts" setup>
import { measureFactory, filterFactory } from '@sisense/sdk-data';
import {
  ThemeProvider,
  LineChart,
} from '@sisense/sdk-ui-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useCallConnectionPoints from '@/composable/user/callResult/call-connection-points';
import { SPEAKER_COLORS } from '@/composable/user/transcription/transcriptionSpeakers';
import * as DM from '@/data-models/bc-call-fb-data-source-V-0-0-1';

type TProps = {
  callerId: number | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  defaultCallResultTypeId: number | null;
  customCallResultTypeId: number | null;
  callConnectionPoint: string | null;
};
const props = defineProps<TProps>();

const i18n = useI18n();
const { callConnectionPoints } = useCallConnectionPoints();

const dataSource = DM.DataSource;

const SPEAKER_TYPE_TEXTS = {
  IS: i18n.t('general.insideSales.shortName'),
  CUSTOMER: i18n.t('general.customer'),
} as const;

const theme = ref({
  general: {
    backgroundColor: '#FAFAFA',
  },
  palette: {
    variantColors: [SPEAKER_COLORS.insideSales],
  },
});

const commonStyleOptions = ref({
  legend: {
    enabled: false,
  },
  line: {
    width: 1,
  },
  markers: {
    enabled: true,
    size: 'small',
    fill: 'hollow',
  },
});

const legendStyleOptions = ref({
  legend: {
    enabled: true,
    align: 'right',
    verticalAlign: 'top',
    floating: true,
  },
});

const targetConnectionPointText = computed(() => {
  return callConnectionPoints.find(point => point.value === props.callConnectionPoint)?.name;
});

const commonFilters = computed(() => {
  return [
    props.callerId ? filterFactory.members(DM.CallResult.CallResult_id, [props.callerId.toString()]) : undefined,
    props.startDate && props.endDate ? filterFactory.dateRange(DM.CallResult.first_set_up_at_jst.Days, props.startDate, props.endDate) : undefined,
    props.defaultCallResultTypeId ? filterFactory.members(DM.CallResult.default_call_result_type_id, [props.defaultCallResultTypeId.toString()]) : undefined,
    props.customCallResultTypeId ? filterFactory.members(DM.CallResult.call_result_type_id, [props.customCallResultTypeId.toString()]) : undefined,
    targetConnectionPointText.value ? filterFactory.members(DM.CallResult.connected_to_text, [targetConnectionPointText.value]) : undefined,
  ].filter(Boolean);
});

const totalTalkSecondsChartProps = computed(() => ({
  dataOptions: {
    category: [DM.CallResult.first_set_up_at_jst.Days],
    value: [
      measureFactory.customFormula(
        i18n.t('training.callFb.talkTime.unit'),
        'Round(Avg([callId],Sum([totalTalkSeconds])) / 60, 2)',
        {
          callId: DM.CallResult.CallResult_id,
          totalTalkSeconds: DM.TranscriptionSpeakerMetric.total_talk_seconds,
        },
      ),
    ],
    breakBy: [],
  },
  filters: commonFilters.value,
  styleOptions: commonStyleOptions.value,
}));

const talkSpeedChartProps = computed(() => ({
  dataOptions: {
    category: [DM.CallResult.first_set_up_at_jst.Days],
    value: [
      measureFactory.average(DM.TranscriptionSpeakerMetric.mora_per_second),
    ],
    breakBy: [DM.TranscriptionSpeakerMetric.speaker_type_text],
    seriesToColorMap: {
      [SPEAKER_TYPE_TEXTS.IS]: SPEAKER_COLORS.insideSales,
      [SPEAKER_TYPE_TEXTS.CUSTOMER]: SPEAKER_COLORS.customer,
    },
  },
  filters: [
    ...commonFilters.value,
    filterFactory.members(DM.TranscriptionSpeakerMetric.speaker_type_text, [SPEAKER_TYPE_TEXTS.IS, SPEAKER_TYPE_TEXTS.CUSTOMER]),
  ],
  styleOptions: {
    ...commonStyleOptions.value,
    ...legendStyleOptions.value,
  },
}));

const speekRatioChartProps = computed(() => ({
  dataOptions: {
    category: [DM.CallResult.first_set_up_at_jst.Days],
    value: [
      {
        column: measureFactory.average(DM.TranscriptionSpeakerMetric.talk_ratio),
        numberFormatConfig: {
          name: 'Percent',
        },
      },
    ],
    breakBy: [DM.TranscriptionSpeakerMetric.speaker_type_text],
    seriesToColorMap: {
      [SPEAKER_TYPE_TEXTS.IS]: SPEAKER_COLORS.insideSales,
      [SPEAKER_TYPE_TEXTS.CUSTOMER]: SPEAKER_COLORS.customer,
    },
  },
  filters: [
    ...commonFilters.value,
    filterFactory.members(DM.TranscriptionSpeakerMetric.speaker_type_text, [SPEAKER_TYPE_TEXTS.IS, SPEAKER_TYPE_TEXTS.CUSTOMER]),
  ],
  styleOptions: {
    ...commonStyleOptions.value,
    ...legendStyleOptions.value,
  },
}));

const questionCountChartProps = computed(() => ({
  dataOptions: {
    category: [DM.CallResult.first_set_up_at_jst.Days],
    value: [
      measureFactory.average(DM.QualitativeFeedbackResult.question_count),
    ],
    breakBy: [],
  },
  filters: commonFilters.value,
  styleOptions: commonStyleOptions.value,
}));

const closingCountChartProps = computed(() => ({
  dataOptions: {
    category: [DM.CallResult.first_set_up_at_jst.Days],
    value: [
      measureFactory.average(DM.QualitativeFeedbackResult.closing_count),
    ],
    breakBy: [],
  },
  filters: commonFilters.value,
  styleOptions: commonStyleOptions.value,
}));

const charts = computed(() => [
  {
    title: `${i18n.t('training.callFb.talkTime.label')} [${i18n.t('training.callFb.talkTime.unit')}]`,
    chartProps: totalTalkSecondsChartProps.value,
  },
  {
    title: `${i18n.t('training.callFb.talkSpeed.label')} [${i18n.t('training.callFb.talkSpeed.unit')}]`,
    chartProps: talkSpeedChartProps.value,
  },
  {
    title: `${i18n.t('training.callFb.talkRatio.label')} [${i18n.t('training.callFb.talkRatio.unit')}]`,
    chartProps: speekRatioChartProps.value,
  },
  {
    title: `${i18n.t('training.callFb.questionCount.label')} [${i18n.t('training.callFb.questionCount.unit')}]`,
    chartProps: questionCountChartProps.value,
  },
  {
    title: `${i18n.t('training.callFb.closingCount.label')} [${i18n.t('training.callFb.closingCount.unit')}]`,
    chartProps: closingCountChartProps.value,
  },
]);
</script>

<style lang="scss" scoped>
.call-fb-charts-container {
  padding: $basespace-300 0;
}

.chart-container {
  width: 100%;
}

.chart-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  font-size: $fontsize-100;
  color: $charcoal;
}

.chart-content {
  width: 100%;
  height: 260px;
}
</style>
